import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';

const AuthContext = createContext(null);

/**
 * Cookie-based authentication context.
 *
 * Authentication tokens are stored ONLY in the httpOnly `ee_session` cookie
 * set by the backend (not accessible to JavaScript). We persist a tiny,
 * non-sensitive `ee_role` flag in localStorage purely so that on app boot
 * we know which /me endpoint to call to rehydrate the session.
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [reseller, setReseller] = useState(null);
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [impersonating, setImpersonating] = useState(() => {
        try { return JSON.parse(localStorage.getItem('ee_impersonating') || 'null'); }
        catch { return null; }
    });

    const refresh = useCallback(async () => {
        const role = localStorage.getItem('ee_role');
        if (!role) { setLoading(false); return; }
        try {
            if (role === 'user') {
                const { data } = await api.get('/auth/me');
                setUser(data.user);
            } else if (role === 'reseller') {
                const { data } = await api.get('/auth/reseller/me');
                setReseller(data.reseller);
            } else if (role === 'admin') {
                const { data } = await api.get('/auth/admin/me');
                setAdmin(data.admin);
            }
        } catch (e) {
            // Cookie missing/expired: clear local role hint
            localStorage.removeItem('ee_role');
            setUser(null); setReseller(null); setAdmin(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // After a successful login/register the backend has already set the
    // httpOnly cookie via Set-Cookie. We only need to remember the role
    // hint and update local state — the `token` arg is ignored (kept in
    // the signature to avoid touching every call-site).
    // eslint-disable-next-line no-unused-vars
    const loginUser = (_token, userData) => {
        localStorage.setItem('ee_role', 'user');
        setUser(userData);
    };
    // eslint-disable-next-line no-unused-vars
    const loginReseller = (_token, data) => {
        localStorage.setItem('ee_role', 'reseller');
        setReseller(data);
    };
    // eslint-disable-next-line no-unused-vars
    const loginAdmin = (_token, data) => {
        localStorage.setItem('ee_role', 'admin');
        setAdmin(data);
    };

    // Admin "login as user" support mode. The backend has already set a user
    // session cookie embedding the admin id; we just flip local state.
    const startImpersonation = (userData, readonly = false) => {
        const info = { name: userData.name, email: userData.email, readonly: !!readonly };
        localStorage.setItem('ee_role', 'user');
        localStorage.setItem('ee_impersonating', JSON.stringify(info));
        setImpersonating(info);
        setAdmin(null);
        setUser(userData);
    };

    const stopImpersonation = async () => {
        try { await api.post('/auth/stop-impersonation'); } catch (_) { /* ignore */ }
        localStorage.removeItem('ee_impersonating');
        localStorage.setItem('ee_role', 'admin');
        setImpersonating(null);
        setUser(null);
        window.location.href = '/admin/users';
    };

    const logout = async () => {
        const role = localStorage.getItem('ee_role');
        try {
            if (role === 'admin') await api.post('/auth/admin/logout');
            else if (role === 'reseller') await api.post('/auth/reseller/logout');
            else await api.post('/auth/logout');
        } catch (_) { /* idempotent; ignore */ }
        localStorage.removeItem('ee_role');
        localStorage.removeItem('ee_impersonating');
        setImpersonating(null);
        // Best-effort cleanup of any legacy keys from the old localStorage flow
        localStorage.removeItem('ee_token');
        localStorage.removeItem('ee_user');
        setUser(null); setReseller(null); setAdmin(null);
        window.location.href = '/';
    };

    return (
        <AuthContext.Provider value={{ user, reseller, admin, loading, refresh, loginUser, loginReseller, loginAdmin, logout, setUser, setReseller, impersonating, startImpersonation, stopImpersonation }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
